import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import { FileText, ArrowRight } from 'lucide-react';

function TermsOfServicesPage() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/apps`);
      if (response.ok) {
        const data = await response.json();
        // Filter apps that have Terms of Service
        const appsWithTerms = data.filter(app => app.termsOfServiceUrl);
        setApps(appsWithTerms);
      }
    } catch (error) {
      console.error('Error fetching apps:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <Header />
      
      <main className="container mx-auto px-4 py-16 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center p-4 bg-blue-500/10 rounded-full border border-blue-500/20 mb-6">
            <FileText className="w-12 h-12 text-blue-400" />
          </div>
          <h1 className="text-5xl font-bold text-white mb-4">
            Terms of Service
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Review the terms of service for each of our applications.
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center text-slate-300">
            <div className="animate-pulse">Loading terms of service...</div>
          </div>
        )}

        {/* No Terms Available */}
        {!loading && apps.length === 0 && (
          <div className="text-center py-16">
            <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 text-lg">
              No terms of service available yet. Check back soon.
            </p>
          </div>
        )}

        {/* Terms Grid */}
        {!loading && apps.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {apps.map((app) => (
              <Link
                key={app.slug}
                to={app.termsOfServiceUrl}
                className="group bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 hover:border-blue-500/50 hover:bg-slate-800/80 transition-all duration-300"
              >
                {/* App Logo */}
                {app.logo && (
                  <div className="mb-4">
                    <img 
                      src={app.logo} 
                      alt={app.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  </div>
                )}

                {/* App Name */}
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">
                  {app.name}
                </h3>

                {/* Category */}
                <p className="text-sm text-slate-400 mb-4">
                  {app.category}
                </p>

                {/* Description Preview */}
                <p className="text-slate-300 text-sm mb-4 line-clamp-2">
                  {app.description}
                </p>

                {/* View Link */}
                <div className="flex items-center gap-2 text-blue-400 font-medium group-hover:gap-3 transition-all">
                  <span>View Terms of Service</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Back to Home */}
        <div className="text-center mt-16">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            ← Back to Home
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/[0.06] bg-slate-950 mt-16">
        <div className="mx-auto max-w-7xl px-6 py-10 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_c83bb9d1-4481-4216-be0b-e2d2e530a3a4/artifacts/j5gcpl1b_Peaceful%20harmony%20in%20a%20logo%20design.png"
              alt="Perfect Peace Enterprise Logo"
              className="h-10 w-auto object-contain"
            />
            <div>
              <div className="text-base font-semibold text-white">
                Perfect Peace Enterprise
              </div>
              <p className="mt-0.5 text-xs text-slate-500">
                Building digital products for real-world impact.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-6 text-sm text-slate-500">
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <Link to="/privacy" className="hover:text-white transition-colors">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-white transition-colors">
              Terms
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default TermsOfServicesPage;
